import { debounce } from '@tanstack/pacer'
import { ChevronUp, ChevronDown, Trash2, Image as ImageIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { ContentBlockKindType, ContentBlockTypeType } from '@/api/admin-marketing-content-types'
import type { Asset } from '@/api/common-types'
import { useListAssets } from '@/api/react-query'
import { AssetImage } from '@/components/base/asset-image'
import { Button } from '@/components/base/button'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Input } from '@/components/base/input'
import { Select } from '@/components/base/select'
import { StatusCell } from '@/components/base/status-cell'
import { Switch } from '@/components/base/switch'
import { Text } from '@/components/base/text'
import { useI18n } from '@/i18n/i18n-react'
import { Plus } from '@/icons/plus'
import { clx } from '@/utils/clx'

export type LinkType = 'NONE' | 'BROWSER' | 'DEEP_LINK' | 'IMAGE'

// The customer-app MarketingContentItem DTO shapes a block's `data` mirrors, modelled here so the editors are
// typed instead of reading/writing `any`. `data` is still stored as JSON server-side (schema `any`),
// but the dashboard only ever produces these shapes.
export type PromotionActionType = 'ADD_LOYALTY_CARD_TO_WALLET' | 'LOYALTY_CARD_INFO'

export type BlockLink = {
	type: Exclude<LinkType, 'NONE'>
	url: string
}

export type BlockImage = {
	// The full Asset (not a denormalized url/width/height snapshot) — mirrors how product images
	// are modelled, and carries `blurhash` through to the customer app for free.
	asset: Asset
	// Optional per-thumbnail tap target. When absent, tapping opens the full-screen image preview
	// (the default, most-common case). IMAGE isn't offered per-thumbnail (that's the default).
	link?: BlockLink
}

export type BannerData = {
	type: 'IMAGE' | 'TEXT_WITH_BACKGROUND'
	image?: BlockImage
	text?: string
}

// List/modal body rows are rendered by the customer app and are not edited in this UI.
export type BlockContentRow = Record<string, unknown>

// Per-block outer spacing. We never expose raw Tailwind: the only lever surfaced in the UI is the
// "join with block above" toggle, which sets the tight tokens on the joined pair. Other values may
// exist in data authored elsewhere and are round-tripped untouched.
export type BlockClassNames = { top?: string; bottom?: string; left?: string; right?: string }

export type BannerBlockData = {
	title?: string
	showTitle?: boolean
	banner: BannerData
	link?: BlockLink
	action?: PromotionActionType
	classNames?: BlockClassNames
}

export type ListThumbnails = {
	items: BlockImage[]
	// Shape of each thumbnail (width/height) and how many fit across the strip. Surfaced as friendly
	// "shape"/"size" presets; a non-integer size keeps a partial next image visible as a scroll hint.
	itemAspectRatio?: number
	containerAspectRatio?: number
	contentContainerClassName?: string
}

export type ListBlockData = {
	title?: string
	showTitle?: boolean
	thumbnails: ListThumbnails
	content?: BlockContentRow[]
	classNames?: BlockClassNames
}

export type ModalBlockData = {
	title?: string
	showTitle?: boolean
	banner: BannerData
	content?: BlockContentRow[]
	classNames?: BlockClassNames
}

export type SeparatorBlockData = {
	title?: string
	showTitle?: boolean
	containerClassName?: string
	classNames?: BlockClassNames
}

export type CustomBlockData = {
	title?: string
	classNames?: BlockClassNames
	[key: string]: unknown
}

// Tight spacing tokens written on a joined pair: the lower block hugs the one above it so the two
// read as a single unit (the common "banner + its image row" case).
export const JOIN_TOP = 'pt-2'
export const JOIN_BOTTOM = 'pb-2'

// Friendly LIST presets. Visible count across the strip ≈ containerAspectRatio / itemAspectRatio, so
// the fractional sizes below always leave a partial next image as a "you can scroll" affordance.
export const LIST_SHAPES = { SQUARE: 1, PORTRAIT: 0.8, LANDSCAPE: 1.5 } as const
export type ListShape = keyof typeof LIST_SHAPES
export const LIST_SIZES = { LARGE: 2.2, MEDIUM: 3.2, SMALL: 4.2 } as const
export type ListSize = keyof typeof LIST_SIZES

// Set/clear one side of a block's classNames, dropping the object entirely when it goes empty so we
// never persist `classNames: {}`.
const withClassName = (block: EditableBlock, side: keyof BlockClassNames, value: string | undefined): EditableBlock => {
	const current = (block.data as { classNames?: BlockClassNames }).classNames ?? {}
	const nextClassNames: BlockClassNames = { ...current }
	if (value === undefined) delete nextClassNames[side]
	else nextClassNames[side] = value
	const hasAny = Object.values(nextClassNames).some(v => v !== undefined)
	return { ...block, data: { ...block.data, classNames: hasAny ? nextClassNames : undefined } }
}

const closestKey = <T extends Record<string, number>>(
	map: T,
	value: number | undefined,
	fallback: keyof T
): keyof T => {
	if (value === undefined) return fallback
	let best = fallback
	let bestDiff = Infinity
	for (const key of Object.keys(map) as (keyof T)[]) {
		const diff = Math.abs(map[key] - value)
		if (diff < bestDiff) {
			bestDiff = diff
			best = key
		}
	}
	return best
}

export type BlockData = BannerBlockData | ListBlockData | ModalBlockData | SeparatorBlockData | CustomBlockData

// A block as edited in the dashboard. STATIC blocks carry a content `type` + `data`; the single
// CAMPAIGN_ZONE block (layouts only) is an anchor with no type and no editable data.
export type EditableBlock = {
	uuid?: string
	kind: ContentBlockKindType
	type?: ContentBlockTypeType
	data: BlockData
}

export const STATIC_BLOCK_TYPES = ['BANNER', 'LIST', 'MODAL', 'SEPARATOR'] as const
export type WritableContentBlockType = (typeof STATIC_BLOCK_TYPES)[number]

export const isWritableBlockType = (type: string | undefined): type is WritableContentBlockType =>
	type === 'BANNER' || type === 'LIST' || type === 'MODAL' || type === 'SEPARATOR'

// A checkout background is exactly one image banner with no tap action - the only shape the
// checkout screen renders and the only one the server accepts for CHECKOUT_BACKGROUND content.
export const checkoutBackgroundBlock = (asset: Asset): EditableBlock => ({
	kind: 'STATIC',
	type: 'BANNER',
	data: { banner: { type: 'IMAGE', image: { asset } } } satisfies BannerBlockData
})

export const checkoutBackgroundAsset = (blocks: EditableBlock[]): Asset | undefined => {
	const banner = (blocks[0]?.data as BannerBlockData | undefined)?.banner
	return banner?.type === 'IMAGE' ? banner.image?.asset : undefined
}

export const defaultDataForType = (type: ContentBlockTypeType): BlockData => {
	switch (type) {
		case 'BANNER':
			return { banner: { type: 'IMAGE' } } satisfies BannerBlockData
		case 'LIST':
			return {
				thumbnails: { items: [], itemAspectRatio: LIST_SHAPES.SQUARE, containerAspectRatio: LIST_SIZES.LARGE },
				content: []
			} satisfies ListBlockData
		case 'MODAL':
			return {
				banner: { type: 'IMAGE' },
				content: []
			} satisfies ModalBlockData
		case 'SEPARATOR':
			return {} satisfies SeparatorBlockData
		case 'CUSTOM':
			return {} satisfies CustomBlockData
	}
}

// The feed renders banners/thumbnails at phone widths, so MEDIUM is the sane default; only fall
// back up to LARGE/ORIGINAL when nothing smaller exists. (Reserve LARGE for full-screen previews.)
const VARIANT_PRIORITY = ['MEDIUM', 'SMALL', 'THUMBNAIL', 'LARGE', 'ORIGINAL'] as const

export const pickVariantUrl = (asset: Asset | undefined): string => {
	if (!asset) return ''
	for (const type of VARIANT_PRIORITY) {
		const match = asset.variants.find(v => v.type === type)
		if (match) return match.url
	}
	return asset.variants[0]?.url ?? ''
}

type BlockListEditorProps = {
	blocks: EditableBlock[]
	onChange: (blocks: EditableBlock[]) => void
	// Layouts allow the single CAMPAIGN_ZONE anchor; campaign placements do not.
	allowCampaignZone?: boolean
}

/**
 * Ordered editor for a list of content blocks. Shared by the campaign placement editor and the
 * feed-layout editor; the only difference is whether the CAMPAIGN_ZONE anchor may be added.
 */
export function BlockListEditor({ blocks, onChange, allowCampaignZone = false }: BlockListEditorProps) {
	const hasZone = blocks.some(b => b.kind === 'CAMPAIGN_ZONE')

	const addStatic = (type: ContentBlockTypeType) => {
		onChange([...blocks, { kind: 'STATIC', type, data: defaultDataForType(type) }])
	}

	const addZone = () => {
		if (hasZone) return
		onChange([...blocks, { kind: 'CAMPAIGN_ZONE', data: {} }])
	}

	const updateBlock = (index: number, next: EditableBlock) => {
		const copy = [...blocks]
		copy[index] = next
		onChange(copy)
	}

	const moveBlock = (index: number, direction: -1 | 1) => {
		const next = index + direction
		if (next < 0 || next >= blocks.length) return
		const copy = [...blocks]
		;[copy[index], copy[next]] = [copy[next], copy[index]]
		onChange(copy)
	}

	const deleteBlock = (index: number) => {
		onChange(blocks.filter((_, i) => i !== index))
	}

	// A block is "joined" to the one above when it hugs it (tight top) and the block above hugs it
	// back (tight bottom). Toggling flips both so the pair reads as one unit — or splits them apart.
	const isJoinedAbove = (index: number) =>
		index > 0 &&
		blocks[index].data.classNames?.top === JOIN_TOP &&
		blocks[index - 1].data.classNames?.bottom === JOIN_BOTTOM

	const toggleJoinAbove = (index: number) => {
		if (index === 0) return
		const joined = isJoinedAbove(index)
		const copy = [...blocks]
		copy[index] = withClassName(copy[index], 'top', joined ? undefined : JOIN_TOP)
		copy[index - 1] = withClassName(copy[index - 1], 'bottom', joined ? undefined : JOIN_BOTTOM)
		onChange(copy)
	}

	return (
		<div className='flex flex-col gap-3'>
			<div className='flex justify-end'>
				<AddBlockMenu onAddStatic={addStatic} onAddZone={allowCampaignZone && !hasZone ? addZone : undefined} />
			</div>
			{blocks.length === 0 ? (
				<EmptyBlocks />
			) : (
				blocks.map((block, index) => (
					<BlockPanel
						key={block.uuid ?? `new-${index}`}
						block={block}
						index={index}
						total={blocks.length}
						joinedAbove={isJoinedAbove(index)}
						onChange={next => updateBlock(index, next)}
						onMoveUp={() => moveBlock(index, -1)}
						onMoveDown={() => moveBlock(index, 1)}
						onToggleJoinAbove={index > 0 ? () => toggleJoinAbove(index) : undefined}
						onDelete={() => deleteBlock(index)}
					/>
				))
			)}
		</div>
	)
}

function AddBlockMenu({
	onAddStatic,
	onAddZone
}: {
	onAddStatic: (type: ContentBlockTypeType) => void
	onAddZone?: () => void
}) {
	const { L } = useI18n()
	const [open, setOpen] = useState(false)
	return (
		<div className='relative'>
			<Button onClick={() => setOpen(!open)} variant='secondary'>
				<Plus className='mr-1 size-3.5' />
				{L.Marketing.Campaign.Action.AddItem()}
			</Button>
			{open && (
				<div className='absolute right-0 top-full z-10 mt-1 w-48 rounded-md border border-ui-border-base bg-ui-bg-base shadow-md'>
					{STATIC_BLOCK_TYPES.map(t => (
						<button
							key={t}
							type='button'
							className='block w-full px-3 py-2 text-left text-sm hover:bg-ui-bg-base-hover'
							onClick={() => {
								onAddStatic(t)
								setOpen(false)
							}}
						>
							{L.Marketing.Campaign.ItemTypeEnum(t)}
						</button>
					))}
					{onAddZone && (
						<button
							type='button'
							className='block w-full border-t border-ui-border-base px-3 py-2 text-left text-sm font-medium hover:bg-ui-bg-base-hover'
							onClick={() => {
								onAddZone()
								setOpen(false)
							}}
						>
							{L.Marketing.Layout.AddCampaignZone()}
						</button>
					)}
				</div>
			)}
		</div>
	)
}

function EmptyBlocks() {
	const { L } = useI18n()
	return (
		<div className='flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-ui-border-base p-8 text-center'>
			<Heading level='h3'>{L.Marketing.Campaign.Item.NoItems()}</Heading>
			<Text className='max-w-sm text-ui-fg-subtle'>{L.Marketing.Campaign.Item.NoItemsDescription()}</Text>
		</div>
	)
}

type BlockPanelProps = {
	block: EditableBlock
	index: number
	total: number
	joinedAbove: boolean
	onChange: (next: EditableBlock) => void
	onMoveUp: () => void
	onMoveDown: () => void
	onToggleJoinAbove?: () => void
	onDelete: () => void
}

function BlockPanel(props: BlockPanelProps) {
	const { L } = useI18n()
	const isZone = props.block.kind === 'CAMPAIGN_ZONE'
	return (
		<div
			className={clx(
				'rounded-md border bg-ui-bg-base',
				// Visually pull a joined panel toward the one above it, mirroring the feed.
				props.joinedAbove ? '-mt-1' : '',
				isZone ? 'border-dashed border-ui-border-interactive' : 'border-ui-border-base'
			)}
		>
			<div className='flex items-center gap-3 border-b border-ui-border-base px-3 py-2'>
				<div className='flex flex-col'>
					<button
						type='button'
						className='text-ui-fg-muted hover:text-ui-fg-base disabled:opacity-30'
						disabled={props.index === 0}
						onClick={props.onMoveUp}
						aria-label={L.Marketing.Campaign.Action.MoveUp()}
					>
						<ChevronUp className='size-3' />
					</button>
					<button
						type='button'
						className='text-ui-fg-muted hover:text-ui-fg-base disabled:opacity-30'
						disabled={props.index === props.total - 1}
						onClick={props.onMoveDown}
						aria-label={L.Marketing.Campaign.Action.MoveDown()}
					>
						<ChevronDown className='size-3' />
					</button>
				</div>
				<StatusCell color={isZone ? 'blue' : 'grey'}>
					{isZone
						? L.Marketing.Layout.CampaignZone()
						: L.Marketing.Campaign.ItemTypeEnum(props.block.type ?? 'CUSTOM')}
				</StatusCell>
				<Text className='min-w-0 flex-1 truncate text-sm text-ui-fg-subtle'>
					{isZone
						? L.Marketing.Layout.CampaignZoneHint()
						: props.block.data?.title
							? String(props.block.data.title)
							: ''}
				</Text>
				{!isZone && props.onToggleJoinAbove && (
					<label className='flex items-center gap-1.5 whitespace-nowrap text-xs text-ui-fg-subtle'>
						<input type='checkbox' checked={props.joinedAbove} onChange={props.onToggleJoinAbove} />
						{L.Marketing.Campaign.JoinAbove()}
					</label>
				)}
				<IconButton
					variant='transparent'
					size='small'
					tooltipText={L.Marketing.Campaign.Action.Delete()}
					onClick={props.onDelete}
				>
					<Trash2 className='size-4' />
				</IconButton>
			</div>
			{!isZone && (
				<div className='p-3'>
					<BlockFormByType block={props.block} onChange={props.onChange} />
				</div>
			)}
		</div>
	)
}

function BlockFormByType({ block, onChange }: { block: EditableBlock; onChange: (next: EditableBlock) => void }) {
	const setData = (next: BlockData) => onChange({ ...block, data: next })
	const data = block.data

	return (
		<div className='flex flex-col gap-3'>
			{block.type !== 'SEPARATOR' && block.type !== 'CUSTOM' && (
				<TitleAndCommonFields data={data} setData={setData} />
			)}
			{block.type === 'BANNER' && <BannerFields data={data as BannerBlockData} setData={setData} />}
			{block.type === 'LIST' && <ListFields data={data as ListBlockData} setData={setData} />}
			{block.type === 'MODAL' && <ModalFields data={data as ModalBlockData} setData={setData} />}
			{block.type === 'SEPARATOR' && <SeparatorFields data={data as SeparatorBlockData} setData={setData} />}
			{block.type === 'CUSTOM' && <CustomFields />}
		</div>
	)
}

export function Field({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
	return (
		<label className='flex flex-col gap-1.5'>
			<Text className='text-xs font-medium text-ui-fg-subtle'>{label}</Text>
			{children}
			{help && <Text className='text-xs text-ui-fg-muted'>{help}</Text>}
		</label>
	)
}

function TitleAndCommonFields({ data, setData }: { data: BlockData; setData: (next: BlockData) => void }) {
	const { L } = useI18n()
	const showTitle = data.showTitle === true
	return (
		<div className='flex flex-col gap-3'>
			<div className='flex items-center justify-between gap-3'>
				<div className='min-w-0'>
					<Text className='text-xs font-medium text-ui-fg-subtle'>{L.Marketing.Campaign.ShowTitle()}</Text>
					<Text className='text-xs text-ui-fg-muted'>{L.Marketing.Campaign.ShowTitleHelp()}</Text>
				</div>
				<Switch
					size='small'
					checked={showTitle}
					onCheckedChange={checked => setData({ ...data, showTitle: checked ? true : undefined })}
				/>
			</div>
			{showTitle && (
				<Field label={L.Marketing.Campaign.Field.Title()}>
					<Input
						value={data.title ?? ''}
						placeholder={L.Marketing.Campaign.Field.ItemTitlePlaceholder()}
						onChange={e => setData({ ...data, title: e.target.value || undefined })}
					/>
				</Field>
			)}
		</div>
	)
}

function BannerFields({ data, setData }: { data: BannerBlockData; setData: (next: BannerBlockData) => void }) {
	const { L } = useI18n()
	const banner: BannerData = data.banner ?? { type: 'IMAGE' }

	return (
		<div className='flex flex-col gap-3'>
			<Select
				value={banner.type}
				onValueChange={t =>
					setData({
						...data,
						banner:
							t === 'IMAGE'
								? { type: 'IMAGE', image: banner.image }
								: { type: 'TEXT_WITH_BACKGROUND', text: '' }
					})
				}
			>
				<Select.Trigger className='w-44'>
					<Select.Value />
				</Select.Trigger>
				<Select.Content>
					<Select.Item value='IMAGE'>{L.Marketing.Campaign.BannerTypeEnum('IMAGE')}</Select.Item>
					<Select.Item value='TEXT_WITH_BACKGROUND'>
						{L.Marketing.Campaign.BannerTypeEnum('TEXT_WITH_BACKGROUND')}
					</Select.Item>
				</Select.Content>
			</Select>
			{banner.type === 'IMAGE' ? (
				<AssetPicker
					selectedAsset={banner.image?.asset}
					onSelect={asset => setData({ ...data, banner: { type: 'IMAGE', image: { asset } } })}
				/>
			) : (
				<Field label={L.Marketing.Campaign.BannerText()}>
					<Input
						value={banner.text ?? ''}
						onChange={e => setData({ ...data, banner: { ...banner, text: e.target.value } })}
					/>
				</Field>
			)}
			<TapActionPicker data={data} setData={setData} />
		</div>
	)
}

function ListFields({ data, setData }: { data: ListBlockData; setData: (next: ListBlockData) => void }) {
	const { L } = useI18n()
	const items = data.thumbnails?.items ?? []
	const [listPickerOpenIndex, setListPickerOpenIndex] = useState<number | null>(null)
	const [multiOpen, setMultiOpen] = useState(false)

	const updateItems = (next: BlockImage[]) =>
		setData({ ...data, thumbnails: { ...(data.thumbnails ?? {}), items: next } })

	const shape = closestKey(LIST_SHAPES, data.thumbnails?.itemAspectRatio, 'SQUARE')
	const size = closestKey(LIST_SIZES, data.thumbnails?.containerAspectRatio, 'LARGE')
	const setThumbMeta = (patch: Partial<ListThumbnails>) =>
		setData({ ...data, thumbnails: { ...(data.thumbnails ?? { items }), ...patch } })

	const assignListThumb = (idx: number, asset: Asset) => {
		const thumbItem = items[idx]
		const next = [...items]
		next[idx] = { ...thumbItem, asset }
		updateItems(next)
		setListPickerOpenIndex(null)
	}

	const appendAssets = (assets: Asset[]) => {
		const added: BlockImage[] = assets.map(asset => ({ asset }))
		updateItems([...items, ...added])
		setMultiOpen(false)
	}

	const setThumbLink = (idx: number, link: BlockLink | undefined) => {
		const next = [...items]
		next[idx] = { ...next[idx], link }
		updateItems(next)
	}

	return (
		<div className='flex flex-col gap-3'>
			<div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
				<Field label={L.Marketing.Campaign.ListImageShape()}>
					<Select
						value={shape}
						onValueChange={v => setThumbMeta({ itemAspectRatio: LIST_SHAPES[v as ListShape] })}
					>
						<Select.Trigger>
							<Select.Value />
						</Select.Trigger>
						<Select.Content>
							{(Object.keys(LIST_SHAPES) as ListShape[]).map(k => (
								<Select.Item key={k} value={k}>
									{L.Marketing.Campaign.ListImageShapeEnum(k)}
								</Select.Item>
							))}
						</Select.Content>
					</Select>
				</Field>
				<Field label={L.Marketing.Campaign.ListImageSize()} help={L.Marketing.Campaign.ListImageSizeHelp()}>
					<Select
						value={size}
						onValueChange={v => setThumbMeta({ containerAspectRatio: LIST_SIZES[v as ListSize] })}
					>
						<Select.Trigger>
							<Select.Value />
						</Select.Trigger>
						<Select.Content>
							{(Object.keys(LIST_SIZES) as ListSize[]).map(k => (
								<Select.Item key={k} value={k}>
									{L.Marketing.Campaign.ListImageSizeEnum(k)}
								</Select.Item>
							))}
						</Select.Content>
					</Select>
				</Field>
			</div>
			<Text className='text-xs text-ui-fg-subtle'>{L.Marketing.Campaign.ListImages()}</Text>
			<div className='flex flex-wrap items-start gap-3'>
				{items.map((thumbItem, idx) => {
					const thumbLinkType: 'NONE' | 'BROWSER' | 'DEEP_LINK' =
						thumbItem.link?.type === 'BROWSER' || thumbItem.link?.type === 'DEEP_LINK'
							? thumbItem.link.type
							: 'NONE'
					return (
						<div
							key={idx}
							className='flex w-40 shrink-0 flex-col gap-1.5 rounded-md border border-ui-border-base p-2'
						>
							<div className='group relative'>
								<AssetPicker
									mode='detached'
									open={listPickerOpenIndex === idx}
									onOpenChange={nextOpen => setListPickerOpenIndex(nextOpen ? idx : null)}
									selectedAsset={thumbItem.asset}
									onSelect={asset => assignListThumb(idx, asset)}
								/>
								<div
									className={clx(
										'absolute right-1 top-1 z-10 rounded-md border border-ui-border-base bg-white p-0.5 shadow-sm',
										'opacity-100 sm:opacity-0 sm:focus-within:opacity-100 sm:group-hover:opacity-100'
									)}
								>
									<IconButton
										variant='transparent'
										size='small'
										tooltipText={L.Marketing.Campaign.Action.Delete()}
										onClick={e => {
											e.stopPropagation()
											const next = items.filter((_, i) => i !== idx)
											setListPickerOpenIndex(prev => {
												if (prev === null) return null
												if (prev === idx) return null
												if (prev > idx) return prev - 1
												return prev
											})
											updateItems(next)
										}}
									>
										<Trash2 className='size-3.5' />
									</IconButton>
								</div>
							</div>
							<Select
								size='small'
								value={thumbLinkType}
								onValueChange={v => {
									if (v === 'NONE') setThumbLink(idx, undefined)
									else
										setThumbLink(idx, {
											type: v as 'BROWSER' | 'DEEP_LINK',
											url: thumbItem.link?.url ?? ''
										})
								}}
							>
								<Select.Trigger aria-label={L.Marketing.Campaign.TapAction()}>
									<Select.Value />
								</Select.Trigger>
								<Select.Content>
									<Select.Item value='NONE'>
										{L.Marketing.Campaign.ThumbnailLinkTypeEnum('NONE')}
									</Select.Item>
									<Select.Item value='BROWSER'>
										{L.Marketing.Campaign.ThumbnailLinkTypeEnum('BROWSER')}
									</Select.Item>
									<Select.Item value='DEEP_LINK'>
										{L.Marketing.Campaign.ThumbnailLinkTypeEnum('DEEP_LINK')}
									</Select.Item>
								</Select.Content>
							</Select>
							{thumbLinkType !== 'NONE' && (
								<Input
									size='small'
									value={thumbItem.link?.url ?? ''}
									placeholder={
										thumbLinkType === 'DEEP_LINK'
											? L.Marketing.Campaign.Link.DeepLink()
											: L.Marketing.Campaign.Link.BrowserUrl()
									}
									onChange={e => setThumbLink(idx, { type: thumbLinkType, url: e.target.value })}
								/>
							)}
						</div>
					)
				})}
				<Button
					variant='secondary'
					size='small'
					className='shrink-0 self-center'
					onClick={() => setMultiOpen(o => !o)}
				>
					<Plus className='mr-1 size-3.5' /> {L.Marketing.Campaign.AddImages()}
				</Button>
			</div>
			{multiOpen ? (
				<MarketingAssetPickGrid onSelect={() => {}} multiSelect={{ onConfirm: appendAssets }} enabled />
			) : null}
			{listPickerOpenIndex !== null && items[listPickerOpenIndex] !== undefined ? (
				<MarketingAssetPickGrid
					selectedAssetUuid={items[listPickerOpenIndex]?.asset?.uuid}
					onSelect={asset => {
						const pickIdx = listPickerOpenIndex
						if (pickIdx === null) return
						assignListThumb(pickIdx, asset)
					}}
					enabled
				/>
			) : null}
		</div>
	)
}

function ModalFields({ data, setData }: { data: ModalBlockData; setData: (next: ModalBlockData) => void }) {
	const { L } = useI18n()
	const banner: BannerData = data.banner ?? { type: 'IMAGE' }
	return (
		<div className='flex flex-col gap-3'>
			<Text className='text-xs text-ui-fg-subtle'>{L.Marketing.Campaign.ModalCover()}</Text>
			<AssetPicker
				selectedAsset={banner.image?.asset}
				onSelect={asset => setData({ ...data, banner: { type: 'IMAGE', image: { asset } } })}
			/>
		</div>
	)
}

function SeparatorFields({ data, setData }: { data: SeparatorBlockData; setData: (next: SeparatorBlockData) => void }) {
	const { L } = useI18n()
	return (
		<Field label={L.Marketing.Campaign.ContainerClassName()}>
			<Input
				value={data.containerClassName ?? ''}
				onChange={e => setData({ ...data, containerClassName: e.target.value || undefined })}
			/>
		</Field>
	)
}

function CustomFields() {
	const { L } = useI18n()
	return <Text className='text-xs text-ui-fg-subtle'>{L.Marketing.Campaign.CustomInfo()}</Text>
}

// A banner's tap behaviour is either navigation (`data.link`) or a built-in action (`data.action`).
// The two are mutually exclusive — the customer app skips link handling when an action is set — so
// they share one grouped control here. Picking one clears the other.
function TapActionPicker({ data, setData }: { data: BannerBlockData; setData: (next: BannerBlockData) => void }) {
	const { L } = useI18n()
	const linkType: LinkType = data.link?.type ?? 'NONE'
	const currentValue: string = data.action ?? linkType
	// BROWSER/DEEP_LINK take a free-text URL. IMAGE ("Image preview") is handled separately below so
	// the common case — enlarge the banner's own image — needs no URL at all.
	const showUrl = data.action === undefined && data.link !== undefined && linkType !== 'NONE' && linkType !== 'IMAGE'
	const bannerImage = data.banner?.type === 'IMAGE' ? data.banner.image : undefined
	const bannerImageUrl = bannerImage ? pickVariantUrl(bannerImage.asset) : undefined
	// The image-preview source is a UI-only choice. We seed it from the stored URL, then track it in
	// state — otherwise picking "Custom" (which starts with an empty URL) would be recomputed back to
	// "Banner" on the next render, so the custom URL input could never appear.
	const [imageSource, setImageSource] = useState<'BANNER' | 'CUSTOM'>(
		data.link?.type === 'IMAGE' && data.link.url && data.link.url !== bannerImageUrl ? 'CUSTOM' : 'BANNER'
	)

	return (
		<div className='flex flex-col gap-3'>
			<Field label={L.Marketing.Campaign.TapAction()} help={L.Marketing.Campaign.TapActionHelp()}>
				<Select
					value={currentValue}
					onValueChange={v => {
						if (v === 'ADD_LOYALTY_CARD_TO_WALLET' || v === 'LOYALTY_CARD_INFO') {
							setData({ ...data, action: v as PromotionActionType, link: undefined })
						} else if (v === 'NONE') {
							setData({ ...data, action: undefined, link: undefined })
						} else {
							setData({
								...data,
								action: undefined,
								link: { type: v as Exclude<LinkType, 'NONE'>, url: data.link?.url ?? '' }
							})
						}
					}}
				>
					<Select.Trigger>
						<Select.Value />
					</Select.Trigger>
					<Select.Content>
						<Select.Group>
							<Select.Label>{L.Marketing.Campaign.TapActionNavigate()}</Select.Label>
							<Select.Item value='NONE'>{L.Marketing.Campaign.Link.TypeEnum('NONE')}</Select.Item>
							<Select.Item value='BROWSER'>{L.Marketing.Campaign.Link.TypeEnum('BROWSER')}</Select.Item>
							<Select.Item value='DEEP_LINK'>
								{L.Marketing.Campaign.Link.TypeEnum('DEEP_LINK')}
							</Select.Item>
							<Select.Item value='IMAGE'>{L.Marketing.Campaign.Link.TypeEnum('IMAGE')}</Select.Item>
						</Select.Group>
						<Select.Group>
							<Select.Label>{L.Marketing.Campaign.TapActionBuiltIn()}</Select.Label>
							<Select.Item value='ADD_LOYALTY_CARD_TO_WALLET'>
								{L.Marketing.Campaign.PromotionActionEnum('ADD_LOYALTY_CARD_TO_WALLET')}
							</Select.Item>
							<Select.Item value='LOYALTY_CARD_INFO'>
								{L.Marketing.Campaign.PromotionActionEnum('LOYALTY_CARD_INFO')}
							</Select.Item>
						</Select.Group>
					</Select.Content>
				</Select>
			</Field>
			{showUrl && (
				<Field
					label={
						linkType === 'DEEP_LINK'
							? L.Marketing.Campaign.Link.DeepLink()
							: L.Marketing.Campaign.Link.BrowserUrl()
					}
				>
					<Input
						value={data.link?.url ?? ''}
						onChange={e =>
							setData({
								...data,
								link: { type: linkType as Exclude<LinkType, 'NONE'>, url: e.target.value }
							})
						}
					/>
				</Field>
			)}
			{data.action === undefined && linkType === 'IMAGE' && (
				<Field
					label={L.Marketing.Campaign.Link.ImageSource()}
					help={L.Marketing.Campaign.Link.ImageSourceHelp()}
				>
					<Select
						value={imageSource}
						onValueChange={value => {
							const src = value as 'BANNER' | 'CUSTOM'
							setImageSource(src)
							setData({
								...data,
								link: {
									type: 'IMAGE',
									url: src === 'BANNER' ? (bannerImageUrl ?? '') : (data.link?.url ?? '')
								}
							})
						}}
					>
						<Select.Trigger>
							<Select.Value />
						</Select.Trigger>
						<Select.Content>
							<Select.Item value='BANNER'>
								{L.Marketing.Campaign.Link.ImageSourceEnum('BANNER')}
							</Select.Item>
							<Select.Item value='CUSTOM'>
								{L.Marketing.Campaign.Link.ImageSourceEnum('CUSTOM')}
							</Select.Item>
						</Select.Content>
					</Select>
				</Field>
			)}
			{data.action === undefined && linkType === 'IMAGE' && imageSource === 'CUSTOM' && (
				<Field label={L.Marketing.Campaign.Link.ImageUrl()}>
					<Input
						value={data.link?.url ?? ''}
						placeholder={L.Marketing.Campaign.Link.ImageUrl()}
						onChange={e => setData({ ...data, link: { type: 'IMAGE', url: e.target.value } })}
					/>
				</Field>
			)}
		</div>
	)
}

type AssetPickerBaseProps = {
	selectedAsset?: Asset
	onSelect: (asset: Asset) => void
}

type AssetPickerProps = AssetPickerBaseProps &
	({ mode?: 'inline' } | { mode: 'detached'; open: boolean; onOpenChange: (open: boolean) => void })

function MarketingAssetPickGrid({
	selectedAssetUuid,
	onSelect,
	enabled = true,
	multiSelect
}: {
	selectedAssetUuid?: string
	onSelect: (asset: Asset) => void
	enabled?: boolean
	// When provided, the grid becomes multi-select: clicking toggles selection and a confirm button
	// adds every selected asset at once (used to add several images to a list in one go).
	multiSelect?: { onConfirm: (assets: Asset[]) => void }
}) {
	const { L } = useI18n()
	const [filenameInput, setFilenameInput] = useState('')
	const [filenameQ, setFilenameQ] = useState('')
	const [selected, setSelected] = useState<Map<string, Asset>>(new Map())
	const debouncedSetFilenameQ = useMemo(() => debounce((value: string) => setFilenameQ(value), { wait: 300 }), [])
	const assetsQuery = useListAssets(
		{ category: ['MARKETING'], q: filenameQ.trim() || undefined, limit: 100, offset: 0 },
		{ enabled }
	)

	const isMulti = multiSelect !== undefined
	const toggle = (asset: Asset) => {
		setSelected(prev => {
			const next = new Map(prev)
			if (next.has(asset.uuid)) next.delete(asset.uuid)
			else next.set(asset.uuid, asset)
			return next
		})
	}

	return (
		<div className='flex max-h-72 flex-col gap-2 rounded-md border border-ui-border-base bg-ui-bg-base p-2'>
			<Input
				type='search'
				size='small'
				placeholder={L.Marketing.LibraryPage.SearchFilename()}
				value={filenameInput}
				onChange={e => {
					setFilenameInput(e.target.value)
					debouncedSetFilenameQ(e.target.value)
				}}
			/>
			<div className='overflow-y-auto'>
				{assetsQuery.isError ? (
					<div className='flex flex-col items-center gap-2 py-4 text-center'>
						<Text className='text-xs text-ui-fg-subtle'>{L.ApiErrorAlert.FailedToLoadData()}</Text>
						<Button type='button' variant='secondary' size='small' onClick={() => assetsQuery.refetch()}>
							{L.General.TryAgain()}
						</Button>
					</div>
				) : assetsQuery.isLoading ? null : (assetsQuery.data?.assets ?? []).length === 0 ? (
					<Text className='py-4 text-center text-xs text-ui-fg-subtle'>
						{L.Marketing.LibraryPage.NoAssets()}
					</Text>
				) : (
					<div className='grid grid-cols-4 gap-2 sm:grid-cols-6'>
						{(assetsQuery.data?.assets ?? []).map(asset => {
							const isSelected = isMulti ? selected.has(asset.uuid) : asset.uuid === selectedAssetUuid
							return (
								<button
									key={asset.uuid}
									type='button'
									className={clx(
										'relative aspect-square overflow-hidden rounded border',
										isSelected
											? 'border-ui-border-interactive ring-2 ring-ui-border-interactive'
											: 'border-ui-border-base'
									)}
									onClick={() => (isMulti ? toggle(asset) : onSelect(asset))}
								>
									<AssetImage asset={asset} preferredVariant='THUMBNAIL' className='h-full w-full' />
									{isMulti && isSelected && (
										<span className='absolute right-1 top-1 rounded-full bg-ui-border-interactive px-1.5 text-[10px] font-semibold text-white'>
											{[...selected.keys()].indexOf(asset.uuid) + 1}
										</span>
									)}
								</button>
							)
						})}
					</div>
				)}
			</div>
			{isMulti && (
				<div className='flex justify-end'>
					<Button
						type='button'
						size='small'
						disabled={selected.size === 0}
						onClick={() => {
							multiSelect.onConfirm([...selected.values()])
							setSelected(new Map())
						}}
					>
						{L.Marketing.Campaign.AddImagesCount({ count: selected.size })}
					</Button>
				</div>
			)}
		</div>
	)
}

export function AssetPicker(props: AssetPickerProps) {
	const isDetached = props.mode === 'detached'
	const [internalOpen, setInternalOpen] = useState(false)
	const open = isDetached ? props.open : internalOpen
	const selectedUrl = props.selectedAsset ? pickVariantUrl(props.selectedAsset) : undefined

	const toggle = () => {
		if (isDetached) props.onOpenChange(!props.open)
		else setInternalOpen(!internalOpen)
	}

	const handleSelectFromGrid = (asset: Asset) => {
		props.onSelect(asset)
		setInternalOpen(false)
	}

	const button = (
		<button
			type='button'
			onClick={toggle}
			className={clx(
				'flex h-20 w-32 items-center justify-center overflow-hidden rounded-md border border-ui-border-base',
				selectedUrl ? 'bg-ui-bg-base' : 'bg-ui-bg-subtle'
			)}
		>
			{selectedUrl ? (
				<img src={selectedUrl} alt='' className='h-full w-full object-cover' />
			) : (
				<ImageIcon className='size-6 text-ui-fg-muted' />
			)}
		</button>
	)

	if (isDetached) return button

	return (
		<div>
			{button}
			{open && (
				<div className='mt-2'>
					<MarketingAssetPickGrid
						selectedAssetUuid={props.selectedAsset?.uuid}
						onSelect={handleSelectFromGrid}
						enabled={open}
					/>
				</div>
			)}
		</div>
	)
}
